import * as React from 'react'
import { cx } from 'class-variance-authority'

type StepperInputProps = {
  /** The label to display above the stepper */
  label?: string | React.ReactNode
  /** The value of the stepper */
  value: number
  /** Callback when value changes */
  onChange: (value: number) => void
  /** Minimum allowed value */
  min?: number
  /** Maximum allowed value */
  max?: number
  /** Custom CSS classes for the container */
  className?: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Input name attribute */
  name?: string
  /** Step value for increments/decrements */
  step?: number
  /** Whether the max value should be set at the current value */
  maxAtInitialValue?: boolean
  /** Input id attribute (used for label association / form a11y) */
  id?: string
  /** Blur handler forwarded from form controller */
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  /** Accessibility description linkage */
  'aria-describedby'?: string
  /** Accessibility invalid state */
  'aria-invalid'?: React.AriaAttributes['aria-invalid']
}

/**
 * A stepper input component that allows users to increment or decrement a number value.
 * Features fluid width, keyboard accessibility, and customizable min/max values.
 */
export const StepperInput = React.forwardRef<HTMLDivElement, StepperInputProps>(
  (
    {
      label,
      value,
      onChange,
      min = 0,
      max = Infinity,
      className,
      disabled = false,
      name,
      step = 1,
      maxAtInitialValue = false,
      id,
      onBlur,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    },
    ref,
  ) => {
    // Store the initial value in a ref
    const initialValueRef = React.useRef<number | null>(
      maxAtInitialValue ? value : Infinity,
    )

    // Calculate effective max value
    const effectiveMax = React.useMemo(() => {
      if (maxAtInitialValue) {
        return Math.min(max, initialValueRef.current)
      }
      return initialValueRef.current
    }, [max, maxAtInitialValue])

    // Handler for increment button
    const handleIncrement = React.useCallback(() => {
      if (value + step <= effectiveMax) {
        onChange(value + step)
      }
    }, [value, effectiveMax, onChange, step])

    // Handler for decrement button
    const handleDecrement = React.useCallback(() => {
      if (value - step >= min) {
        onChange(value - step)
      }
    }, [value, min, onChange, step])

    // Handler for direct input changes
    const handleInputChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(e.target.value)
        if (!isNaN(newValue) && newValue >= min && newValue <= effectiveMax) {
          onChange(newValue)
        }
      },
      [min, effectiveMax, onChange],
    )

    // Handler for keyboard interactions
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          handleIncrement()
        } else if (e.key === 'ArrowDown') {
          e.preventDefault()
          handleDecrement()
        }
      },
      [handleIncrement, handleDecrement],
    )

    // Add new handler for input focus
    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select()
      },
      [],
    )

    return (
      <div ref={ref} className={cx('flex flex-col space-y-2', className)}>
        {label && (
          <label
            htmlFor={name}
            className='text-sm font-semibold text-slate-500'
          >
            {label}
          </label>
        )}
        <div
          className={cx(
            'group relative flex rounded-md bg-transparent border border-border',
            'focus-within:ring-1 focus-within:ring-primary-500 focus-within:ring-offset-2',
            disabled && 'opacity-50',
          )}
        >
          <button
            type='button'
            onClick={handleDecrement}
            disabled={disabled || value <= min}
            className={cx(
              'flex h-full items-center justify-center rounded-l px-4 py-1 text-2xl font-thin',
              'bg-neutral-100 text-slate-300 transition-colors',
              'hover:bg-neutral-200 hover:text-slate-500 active:bg-neutral-400',
              'focus:outline-none',
              'disabled:cursor-not-allowed',
            )}
            aria-label='Decrease value'
          >
            −
          </button>
          <input
            type='number'
            id={id ?? name}
            name={name}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={onBlur}
            disabled={disabled}
            min={min}
            max={effectiveMax}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            className={cx(
              'w-full bg-white px-3 text-center text-slate-500',
              'focus:outline-none focus:ring-transparent border-none',
              'disabled:cursor-not-allowed',
              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            )}
          />
          <button
            type='button'
            onClick={handleIncrement}
            disabled={disabled || value >= effectiveMax}
            className={cx(
              'flex h-full items-center justify-center rounded-r px-4 py-1 text-2xl font-thin',
              'bg-neutral-100 text-slate-300 transition-colors',
              'hover:bg-neutral-200 hover:text-slate-500 active:bg-neutral-400',
              'focus:outline-none',
              'disabled:cursor-not-allowed',
            )}
            aria-label='Increase value'
          >
            +
          </button>
        </div>
      </div>
    )
  },
)

StepperInput.displayName = 'StepperInput'
